import React from 'react';
import { REPORT_DOWNLOAD_MODES } from '../utils/reportDownload';

export const AiReportModePanel = ({
  access,
  selectedMode,
  onModeChange,
  labels = {},
}) => {
  if (!access?.canDownloadAiReport) {
    return null;
  }

  const resolvedMode = access.isAiModeLocked
    ? REPORT_DOWNLOAD_MODES.AI
    : selectedMode === REPORT_DOWNLOAD_MODES.BOTH && access.canDownloadCombinedReport
      ? REPORT_DOWNLOAD_MODES.BOTH
    : selectedMode === REPORT_DOWNLOAD_MODES.AI
      ? REPORT_DOWNLOAD_MODES.AI
      : REPORT_DOWNLOAD_MODES.STANDARD;

  const isAiActive =
    resolvedMode === REPORT_DOWNLOAD_MODES.AI ||
    resolvedMode === REPORT_DOWNLOAD_MODES.BOTH;
  const badgeText = access.isAiModeLocked
    ? labels.badgeAiOnly || 'AI PDF only'
    : resolvedMode === REPORT_DOWNLOAD_MODES.BOTH
      ? labels.badgeCombinedEnabled || 'PDF + CSV selected'
    : resolvedMode === REPORT_DOWNLOAD_MODES.AI
      ? labels.badgeAiEnabled || 'AI PDF selected'
      : labels.badgeStandardEnabled || 'Standard ZIP selected';
  const helperText = access.isAiModeLocked
    ? labels.lockedDescription ||
      'Your role allows only the AI-generated PDF for this report.'
    : resolvedMode === REPORT_DOWNLOAD_MODES.BOTH
      ? labels.combinedDescription ||
        'Download one ZIP package that includes both the styled AI PDF and the original CSV export.'
    : resolvedMode === REPORT_DOWNLOAD_MODES.AI
      ? labels.aiDescription ||
        'Only the AI report PDF will be downloaded. The normal ZIP report will not be included.'
      : labels.standardDescription ||
        'Download only the original ZIP export that contains the CSV report data.';

  const chips = resolvedMode === REPORT_DOWNLOAD_MODES.BOTH
    ? [
        labels.chipPdf || 'Styled PDF',
        labels.chipCsv || 'Original CSV',
        labels.chipBundle || 'Single ZIP package',
      ]
    : resolvedMode === REPORT_DOWNLOAD_MODES.AI
    ? [
        labels.chipPdf || 'Styled PDF',
        labels.chipAiNarrative || 'AI narrative',
        labels.chipReportText || 'report_text only',
      ]
    : [
        labels.chipZip || 'ZIP export',
        labels.chipCsv || 'Original CSV',
        labels.chipRawData || 'Raw report data',
      ];

  const handleModeChange = (nextMode) => {
    if (access.isAiModeLocked) {
      return;
    }

    onModeChange?.(nextMode);
  };

  return (
    <div
      className={`dr-aiPanel${isAiActive ? ' is-ai-active' : ''}${
        access.isAiModeLocked ? ' is-locked' : ''
      }`}
    >
      <div className="dr-aiPanel-header">
        <div>
          <div className="dr-aiPanel-kicker">
            {labels.kicker || 'AI Report Delivery'}
          </div>
          <h3 className="dr-aiPanel-title">
            {labels.title || 'Choose report download mode'}
          </h3>
          <p className="dr-aiPanel-copy">{helperText}</p>
        </div>

        <div className="dr-aiPanel-toggleWrap">
          <span className="dr-aiPanel-badge">{badgeText}</span>
          <div
            className="dr-aiPanel-modeSwitch"
            role="tablist"
            aria-label={labels.modeSwitcherAria || 'Report download mode'}
          >
            {access.canDownloadStandardReport && (
              <button
                type="button"
                className={`dr-aiPanel-modeBtn${
                  resolvedMode === REPORT_DOWNLOAD_MODES.STANDARD ? ' is-active' : ''
                }`}
                onClick={() => handleModeChange(REPORT_DOWNLOAD_MODES.STANDARD)}
                disabled={access.isAiModeLocked}
              >
                {labels.standardOption || 'Standard ZIP'}
              </button>
            )}

            {access.canDownloadCombinedReport && (
              <button
                type="button"
                className={`dr-aiPanel-modeBtn${
                  resolvedMode === REPORT_DOWNLOAD_MODES.BOTH ? ' is-active' : ''
                }`}
                onClick={() => handleModeChange(REPORT_DOWNLOAD_MODES.BOTH)}
                disabled={access.isAiModeLocked}
              >
                {labels.combinedOption || 'PDF + CSV'}
              </button>
            )}

            <button
              type="button"
              className={`dr-aiPanel-modeBtn${
                resolvedMode === REPORT_DOWNLOAD_MODES.AI ? ' is-active' : ''
              }`}
              onClick={() => handleModeChange(REPORT_DOWNLOAD_MODES.AI)}
              disabled={access.isAiModeLocked}
            >
              {labels.aiOption || 'AI PDF'}
            </button>
          </div>
        </div>
      </div>

      <div className="dr-aiPanel-chipRow">
        {chips.map((chip) => (
          <span key={chip} className="dr-aiPanel-chip">
            {chip}
          </span>
        ))}
      </div>
    </div>
  );
};

export default AiReportModePanel;
